
import { defineComponent } from "vue";
import { store } from "@/store";
import { GeneralActionTypes } from "@/store/modules/general/action-types";
import { Field, Form, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import Swal from "sweetalert2";
import api from "@/store/axiosAgsc";
import { useI18n } from "vue-i18n";
import Toggle from "@vueform/toggle";
import AgCopyPaste from "@/components/ag-copy-paste/index.vue";
import { Roles } from "@/helpers/enums/Roles";

let formData : any;

export default defineComponent({
  name: "SSOSettingsForm",
  components: { Field, Form, ErrorMessage, Toggle, AgCopyPaste },
  setup() {
    const { t, locale } = useI18n();
    return {
      t,
    };
  },
  data() {
    const schema = Yup.object().shape({
      enabled: Yup.boolean().label(this.t("enabled")),
      companyShortName: Yup.string()
        .when('isCompanyScope', {
          is: true,
          then: Yup.string().required().label(this.t("companyShortName")),
        }),
      displayName: Yup.string().required().label(this.t("provider_name")),
      providerType: Yup.string().required().label(this.t("providerType")),
      clientId: Yup.string().required().label(this.t("clientId")),
      objectId: Yup.string().required().label(this.t("objectId")),
      directoryId: Yup.string().required().label(this.t("directoryId")),
    });

    const googleSchema = Yup.object().shape({
      enabled: Yup.boolean().label(this.t("enabled")),
      companyShortName: Yup.string()
        .when('isCompanyScope', {
          is: true,
          then: Yup.string().required().label(this.t("companyShortName")),
        }),
      displayName: Yup.string().required().label(this.t("provider_name")),
      providerType: Yup.string().required().label(this.t("providerType")),
      clientId: Yup.string().required().label(this.t("clientId")),
      secret: Yup.string().required().label(this.t("secret")),
    });

    const oktaSchema = Yup.object().shape({
      enabled: Yup.boolean().label(this.t("enabled")),
      companyShortName: Yup.string()
        .when('isCompanyScope', {
          is: true,
          then: Yup.string().required().label(this.t("companyShortName")),
        }),
      displayName: Yup.string().required().label(this.t("provider_name")),
      providerType: Yup.string().required().label(this.t("providerType")),
      clientId: Yup.string().required().label(this.t("clientId")),
      secret: Yup.string().required().label(this.t("secret")),
      authUrl: Yup.string()
    .required()
    .test("is-https", "Authentication URL must start with 'https://'", (value) => {
      if (value && !value.startsWith("https://")) {
        return false; 
      }
      return true;
    })
    .label(this.t("authUrl")),

    });

    return {
      isEnable: true,
      schema,
      googleSchema,
      oktaSchema,
      isCompanyScope: false,
      formValues: {
        enabled: true,
        companyShortName: "",
        displayName: "",
        providerType: "Microsoft",
        clientId: "",
        objectId: "",
        directoryId: "",
        secret: "",
        authUrl: ""
      },
    };
  },
  props: {
    type: {
      type: String,
      default: null,
    },
    data: {
      type: Object,
      default: null,
    },
  },
  watch: {
    data() {
      this.setData();
    },

  },
  computed: {
    companyDomain() : any {
      if (this.data && this.data.id) {
        let domain = this.splitHostname();
        return `${this.data.companyShortName}.${domain.domain}.${domain.type}`;
      }
      return false;
    },
    getCompany() : any {
      let companyString = localStorage.getItem("company");
      let company: any;

      if (companyString !== null)
        company = JSON.parse(companyString);
      return company;
    },
    getPartner() : any {
      let partnerString = localStorage.getItem("partner");
      let partner: any;

      if (partnerString !== null)
        partner = JSON.parse(partnerString);
      return partner;
    },
    localStorage() {
      return localStorage;
    },
    registerUri(): any {
      const company = store.getters.company;
      let guid;

      if(this.isCompanyScope)
        guid = company.companyGuid;
      else if(this.getPartner && this.localStorage.selectedRole == Roles.PartnerAdmin)
        guid = this.getPartner.guid;
      else
        guid = process.env.VUE_APP_GLOBAL_ADMIN_SSO_SCHEME_ID;

      return `${process.env.VUE_APP_IDENTITY_URL}/federation/${this.formValues.providerType}-${guid}/signin`;
    },
  },
  mounted() {
    this.setData();
    if(this.getCompany.companyGuid && this.localStorage.selectedRole == Roles.CompanyAdmin) {
      this.isCompanyScope = true;
      this.getCompanyShortName();
    }
  },
  methods: {
    getCompanyShortName() {
      const company = store.getters.company;
      
      api.get("Company/GetCompanyDetails?companyGuid=" + company.companyGuid, null)
        .then((response) => {
          if (Number(response.data.statusCode) != 200) {
            Swal.fire("Error", response.data.errors[0], "error");
          } else {
            this.formValues.companyShortName = response.data.entityData.companyShortName;
          }
        });
    },

    splitHostname() {
      var result: any = {};
      var regexParse = new RegExp("([a-z-0-9]{2,63}).([a-z.]{2,5})$");
      var urlParts: any = regexParse.exec(window.location.hostname);
      result.domain = urlParts[1];
      result.type = urlParts[2];
      result.subdomain = window.location.hostname
        .replace(result.domain + "." + result.type, "")
        .slice(0, -1);
      return result;
    },
    setData() {
      if (this.data && this.data.id) {
        if(this.isCompanyScope)
          this.formValues.companyShortName = this.data.companyShortName;
        this.formValues.displayName = this.data.displayName;
        this.formValues.providerType = this.data.providerType;
        this.formValues.clientId = this.data.clientId;
        this.formValues.objectId = this.data.objectId;
        this.formValues.directoryId = this.data.directoryId;
        this.formValues.secret = this.data.secret;
        this.isEnable = this.data.enabled;
        this.formValues.enabled = this.data.enabled;
        this.formValues.authUrl = this.data.authUrl;
      }
    },
    async toggleStatus(value: any) {
      this.formValues.enabled = value;
    },
    deleteProvider() {
      Swal.fire({
        title: "Are You Sure?",
        icon: "warning",
        text: "You are about to delete the selected item!",
        showCancelButton: true,
        confirmButtonText: "Delete",
      }).then((result) => {
        if (result.isConfirmed) {
          let formData = {};
          if (result.isConfirmed) {
          if(this.isCompanyScope) {
            const company = store.getters.company;
            formData = {
              companyGuid: company.companyGuid,
              providerType: this.formValues.providerType,
              companyShortName: this.formValues.companyShortName
            };
          } else if (this.getPartner && localStorage.selectedRole == Roles.PartnerAdmin) {
            formData = {
              partnerGuid: this.getPartner.guid,
              providerType: this.formValues.providerType,
            };
          } else {
            formData = {
              globalAdminSSOSchemeId: process.env.VUE_APP_GLOBAL_ADMIN_SSO_SCHEME_ID,
              providerType: this.formValues.providerType,
            };
          }
        }
          api
            .post("Company/DeleteExternalProvider", formData)
            .then((response: any) => {
              if (Number(response.data.statusCode) != 200) {
                Swal.fire("Error", response.errors[0], "error");
              } else {
                Swal.fire(
                  "Success!",
                  "SSO provider has been successfully deleted!",
                  "success"
                );
                this.$emit("deleted");
              }
            })
            .catch((e: any) => {
              Swal.fire("Error", JSON.stringify(e.Message), "error");
            });
        }
      });
    },
    onSubmit(values: any) {
      Swal.fire({
        title: "Are You Sure?",
        text:
          this.type === "create"
            ? "You are about to create the SSO Provider!"
            : "You are about to update the SSO Provider!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ok",
      }).then((result) => {
     
        if (result.isConfirmed) {
          if(this.isCompanyScope) {
            const company = store.getters.company;
            formData = {
              ...values,
              companyGuid: company.companyGuid,
              companyName: company.companyName,
            };
          } else if (this.getPartner && localStorage.selectedRole == Roles.PartnerAdmin) {
            formData = {
              ...values,
              partnerGuid: this.getPartner.guid,
              partnerName: this.getPartner.name,
            };
          } else {
            formData = {
              ...values,
              globalAdminSSOSchemeId: process.env.VUE_APP_GLOBAL_ADMIN_SSO_SCHEME_ID
            };
          }
          if (this.data && this.data.id) {
            formData.id = this.data.id;
          }
          api
    .post("Company/CreateEditExternalProvider", formData)
    .then((response: any) => {
              if (Number(response.status) != 200) {
                Swal.fire("Error", response.Errors, "error");
              } else {
            Swal.fire(
                "Success!",
                this.type === "create"
                    ? "SSO provider has been successfully created!"
                    : "SSO provider has been successfully updated!",
                "success"
            );
            if (this.type === "create") {
                this.$router.push("/settings/sso-settings");
            }
        }
    })
    .catch((e: any) => {
              Swal.fire("Error", JSON.stringify(e.Message), "error");
            });
        }
      });
    },
  },
});
